/**
 * DataBest — Voice result panel ("Esto entendí").
 * Shows AI-interpreted task reports (saved / needs review) + incidents.
 */
import React, { useState } from "react";
import { Sparkles, CheckCircle2, AlertTriangle, X, Undo2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { voiceApi } from "@/features/voice/api/voiceApi";

const CHIP = {
    H: "bg-brand-green-100 text-brand-green-800 border-brand-green-200",
    M: "bg-amber-100 text-amber-800 border-amber-200",
    L: "bg-red-100 text-red-800 border-red-200",
};

const ScoreChip = ({ label, value }) =>
    value ? (
        <span
            className={`inline-flex items-center gap-0.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                CHIP[value] || "bg-slate-100 text-slate-700 border-slate-200"
            }`}
        >
            <span className="opacity-60">{label}</span>
            <span className="font-black">{value}</span>
        </span>
    ) : (
        <span className="inline-flex items-center gap-0.5 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-400">
            <span className="opacity-60">{label}</span>—
        </span>
    );

const VoiceResultPanel = ({ result, onClose, onReviewTask }) => {
    const [undoing, setUndoing] = useState(false);
    const [undone, setUndone] = useState(false);
    if (!result) return null;

    const saved = result.saved || [];
    const incidents = result.incidents || [];
    const s = result.summary || {};

    const handleUndo = async () => {
        setUndoing(true);
        try {
            await voiceApi.undo({
                measurement_ids: result.undo?.measurements || [],
                incident_ids: result.undo?.incidents || [],
            });
            setUndone(true);
            toast.success("Reporte por voz deshecho");
        } catch (err) {
            toast.error(err?.message || "No se pudo deshacer");
        } finally {
            setUndoing(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
            <div
                data-testid="voice-result-panel"
                className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl sm:rounded-3xl"
            >
                <div className="mb-3 flex items-start justify-between">
                    <div className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-brand-blue-700" />
                        <h3 className="font-heading text-lg font-bold text-brand-blue-950">
                            Esto entendí
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="voice-result-close"
                        className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <p
                    data-testid="voice-result-summary"
                    className="mb-4 text-sm text-slate-500"
                >
                    {s.tasks_saved || 0} tareas registradas · {s.needs_review || 0} por revisar · {s.incidents || 0} incidentes
                    {s.unmatched ? ` · ${s.unmatched} sin coincidencia` : ""}
                </p>

                {saved.length === 0 && incidents.length === 0 && (
                    <p className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-600">
                        No se reconoció ninguna tarea o incidente. Intenta describir con más detalle.
                    </p>
                )}

                <div className="space-y-2.5">
                    {saved.map((r) => {
                        const review = r.status === "needs_review";
                        return (
                            <div
                                key={r.task_id}
                                data-testid={`voice-task-row-${r.task_id}`}
                                className={`rounded-2xl border p-3 ${
                                    review
                                        ? "border-l-4 border-amber-300 bg-amber-50/50"
                                        : "border-slate-200 bg-white"
                                }`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <p className="text-sm font-semibold text-slate-800">
                                        {r.title}
                                    </p>
                                    {review ? (
                                        <button
                                            onClick={() => onReviewTask?.(r.task_id)}
                                            data-testid={`voice-task-status-${r.task_id}`}
                                            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-amber-800 hover:bg-amber-200"
                                        >
                                            <Pencil className="h-3 w-3" /> Revisar
                                        </button>
                                    ) : (
                                        <span
                                            data-testid={`voice-task-status-${r.task_id}`}
                                            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-brand-green-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-brand-green-800"
                                        >
                                            <CheckCircle2 className="h-3 w-3" /> Guardado
                                        </span>
                                    )}
                                </div>
                                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                    <ScoreChip label="D" value={r.disposition_score} />
                                    <ScoreChip label="Q" value={r.quality_score} />
                                    <ScoreChip label="A" value={r.autonomy_score} />
                                    <span
                                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                                            r.response_to_supports
                                                ? "bg-brand-green-100 text-brand-green-800 border-brand-green-200"
                                                : r.response_to_supports === false
                                                    ? "bg-red-100 text-red-800 border-red-200"
                                                    : "bg-slate-50 text-slate-400 border-slate-200"
                                        }`}
                                    >
                                        {r.response_to_supports === true
                                            ? "Sí"
                                            : r.response_to_supports === false
                                                ? "No"
                                                : "—"}
                                    </span>
                                    {r.was_update && (
                                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                            actualizado
                                        </span>
                                    )}
                                    {(r.inferred_dims || []).length > 0 && (
                                        <span className="text-[10px] italic text-slate-400">
                                            (inferido: {r.inferred_dims.join(", ")})
                                        </span>
                                    )}
                                </div>
                                {r.comment && (
                                    <p className="mt-1.5 text-xs text-slate-600">{r.comment}</p>
                                )}
                                {r.sanitized && (
                                    <p className="mt-1 text-[11px] text-amber-700">
                                        Se ajustó el lenguaje del comentario.
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>

                {incidents.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                        {incidents.map((inc, i) => (
                            <div
                                key={i}
                                data-testid={`voice-incident-row-${i}`}
                                className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3"
                            >
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                                <div>
                                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                                        Incidente guardado para el reporte
                                        {inc.persisted === false ? " (pendiente de migración)" : ""}
                                    </p>
                                    <p className="text-sm text-slate-700">{inc.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="mt-5 flex items-center gap-3">
                    <button
                        onClick={onClose}
                        data-testid="voice-done-button"
                        className="h-12 flex-1 rounded-2xl bg-brand-blue-700 text-base font-bold uppercase tracking-wider text-white hover:bg-brand-blue-800"
                    >
                        Listo
                    </button>
                    {!undone && (result.undo?.measurements?.length > 0 || result.undo?.incidents?.length > 0) && (
                        <button
                            onClick={handleUndo}
                            disabled={undoing}
                            data-testid="voice-undo-button"
                            className="inline-flex items-center gap-1.5 rounded-2xl px-3 py-2 text-sm font-semibold text-slate-500 hover:text-red-700 disabled:opacity-50"
                        >
                            <Undo2 className="h-4 w-4" />
                            {undoing ? "Deshaciendo…" : "Deshacer"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VoiceResultPanel;
